import { useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadSection from "../../UploadSection";
import type { PhotoCaptureProps } from "../../../types";

export default function PhotoCapture({ onPhotoCapture, onBack }: PhotoCaptureProps) {
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [openFrontImage, setOpenFrontImage] = useState(true);
  const { t } = useTranslation();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFrontImage(file);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        minHeight="60vh"
        py={3}
      >
        <Typography
          variant="h4"
          sx={{ color: "#7479AE", fontWeight: 700, mb: 4, textAlign: "center" }}
        >
          {t("admin.checkin.identityVerification")}
        </Typography>
        <UploadSection
          label={t("admin.checkin.patientPhotoLabel")}
          image={frontImage}
          open={openFrontImage}
          setOpen={setOpenFrontImage}
          inputId="upload-patient-photo"
          onFileChange={handleFileChange}
          onCheckboxClick={() => {
            if (!openFrontImage || !frontImage) {
              document.getElementById("upload-patient-photo")?.click();
            }
          }}
          delay={0.3}
        />

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 4 }}>
          <Button
            variant="outlined"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{ 
              color: "#7479AE", 
              borderColor: "#7479AE",
              px: 4,
              py: 1.5,
              borderRadius: 3,
            }}
          >
            {t("admin.checkin.back")}
          </Button>

          <Button
            variant="contained"
            disabled={!frontImage}
            onClick={() => {
              if (frontImage) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  const result = e.target?.result as string;
                  onPhotoCapture(result);
                };
                reader.readAsDataURL(frontImage);
              }
            }}
            sx={{
              bgcolor: "#7479AE",
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: 3,
              "&:hover": {
                bgcolor: "#5a5f8a",
              },
              "&:disabled": {
                bgcolor: "#ccc",
              },
            }}
          >
            {t("admin.checkin.confirmPhoto")}
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
}
